import { configureStore } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import editorSlice from "./slices/editor";
import notation from "./slices/notation";
import { notationSaveListener } from "./middleware/notationSaveListener";

/**
 * Application store.
 *
 * `react-redux-firebase`'s `firebaseReducer` used to be mounted here under a
 * `firebase` key. Its provider was never actually installed (the
 * `ReactReduxFirebaseProvider` import in App.tsx was commented out), so nothing
 * ever dispatched into it and `state.firebase.auth.isLoaded` was frozen at
 * `false` forever. Two components gated on that value — see the note in
 * EmailVerification. The dependency is now gone.
 */
const rootReducer = combineReducers({
    editorSlice: editorSlice,
    // Open notation documents. Replaces the static EditorFile.Instances registry.
    notation: notation,
});

export type RootState = ReturnType<typeof rootReducer>;

/**
 * Creates a fresh store.
 *
 * Exported as a factory so a server can build one **per request**: a single
 * module-level store would be shared by every concurrent user on Node, which is
 * how one person's document could end up rendered for another. The browser
 * still uses the single instance below, and the services that need a store
 * import that one directly — wiring them to an injected store is part of the
 * Next.js migration, not of this refactor.
 */
export function makeStore() {
    return configureStore({
        reducer: rootReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                // Firestore Timestamps and FieldValues are not plain objects.
                serializableCheck: false,
            })
                // Persistence reacts to notation actions rather than being
                // called from components. Prepended so it observes every
                // mutation before other middleware can alter the action.
                .prepend(notationSaveListener.middleware),
        devTools: process.env.NODE_ENV !== "production",
    });
}

const store = makeStore();

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore["dispatch"];
export default store;
